/*
  custom-page-scroll-indicator.js: auto-hiding page scroll indicator (styles in
  custom-page-scroll-indicator.css).

  The native page scrollbar is hidden so full-bleed sections reach the viewport edge,
  and iOS does not bring its own indicator back once a style has hidden it. This draws
  one instead, on desktop and touch alike: a thumb that mirrors the page position while
  scrolling, fades after IDLE_MS of rest, and stays off while a pinned runway marked
  [data-scroll-indicator-off] fills the viewport (the craft story scrollytelling), so
  it never sits on top of a full-screen story. The document is the scroller here, so
  window.scrollY is the position.
*/
(function () {
  'use strict';

  if (window.__rsPageScrollIndicator) return;
  window.__rsPageScrollIndicator = true;

  var IDLE_MS = 900;
  var MIN_THUMB = 28;
  // After a drawer closes, the scroll restore is followed by a short settle that nudges
  // scrollY a few more pixels (measured on the home page: +19 px then back, within
  // ~40 ms); this window absorbs it with margin.
  var UNLOCK_SETTLE_MS = 400;
  var OFF_SELECTOR = '[data-scroll-indicator-off]';
  // Body classes that mean a drawer has locked the page. custom-cart-scroll-lock.js pins
  // the body (position: fixed; top: -savedY) while Shrine's overflow-hidden is on, which
  // collapses the document, so scrollY jumps savedY -> 0 on open and back on close. The
  // -mobile / -tablet / -desktop variants are Shrine's breakpoint-scoped locks; the menu
  // drawer sets -desktop, which has no CSS, so the page still scrolls behind the open
  // menu and the thumb would paint over its scrim.
  var LOCK_CLASSES = ['drawer-scroll-locked', 'overflow-hidden', 'overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden-desktop'];

  var root = document.documentElement;
  var body = document.body;
  if (!body) return;

  // iOS before Safari 18.2 cannot hide its own page indicator (no scrollbar-width, and
  // ::-webkit-scrollbar does not reach the page scroller there), so a drawn thumb would
  // double it. Those phones keep the native overlay bar, which also fades when idle.
  var isIOS = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS && !(window.CSS && CSS.supports('scrollbar-width', 'none'))) return;

  var bar = document.createElement('div');
  bar.className = 'page-scroll-indicator';
  bar.setAttribute('aria-hidden', 'true');
  var thumb = document.createElement('i');
  thumb.className = 'page-scroll-indicator__thumb';
  bar.appendChild(thumb);
  body.appendChild(bar);

  // Queried once, not on every scroll frame; queried again when the theme editor
  // reloads a section or a cached node has been replaced.
  var offEls = null;
  document.addEventListener('shopify:section:load', function () {
    offEls = null;
  });

  function isLocked() {
    for (var i = 0; i < LOCK_CLASSES.length; i++) {
      if (body.classList.contains(LOCK_CLASSES[i])) return true;
    }
    return false;
  }

  function offElements() {
    var stale = !offEls;
    for (var i = 0; !stale && i < offEls.length; i++) {
      if (!offEls[i].isConnected) stale = true;
    }
    if (stale) offEls = Array.prototype.slice.call(document.querySelectorAll(OFF_SELECTOR));
    return offEls;
  }

  // A runway holds the viewport when it starts at or above the top edge and ends at or
  // below the bottom edge. Reduced-motion fallbacks hide the runway (height 0), so they
  // never count.
  function runwayFillsViewport(vh) {
    var els = offElements();
    for (var i = 0; i < els.length; i++) {
      var r = els[i].getBoundingClientRect();
      if (r.height > 0 && r.top <= 1 && r.bottom >= vh - 1) return true;
    }
    return false;
  }

  var lastTop = window.scrollY || 0;
  var lastH = -1;
  var lastY = -1;
  var wasLocked = false;
  var settleUntil = 0;
  var idle = 0;
  var raf = 0;

  // Skip the class write when the state is unchanged, so steady scroll frames never
  // touch the element's attributes (the cookie-banner detector watches mutations).
  function setState(name, on) {
    if (bar.classList.contains(name) !== on) bar.classList.toggle(name, on);
  }

  function hide() {
    setState('is-active', false);
  }

  function paint() {
    raf = 0;

    if (isLocked()) {
      // Keep lastTop at the pre-lock position: the whipsaw to 0 and back is not a
      // user scroll and must not light the bar.
      wasLocked = true;
      clearTimeout(idle);
      hide();
      return;
    }

    // Layout reads first, style writes after, so a frame never forces a second layout.
    var top = window.scrollY || 0;
    var vh = window.innerHeight;
    var max = root.scrollHeight - vh;
    var track = bar.clientHeight;
    var off = max > 0 && track > 0 && runwayFillsViewport(vh);

    if (max <= 0 || track <= 0) {
      lastTop = top;
      clearTimeout(idle);
      hide();
      return;
    }

    var h = Math.max(MIN_THUMB, Math.round((track * vh) / (max + vh)));
    var y = Math.round((track - h) * Math.min(1, Math.max(0, top / max)));
    if (h !== lastH) {
      thumb.style.height = h + 'px';
      lastH = h;
    }
    if (y !== lastY) {
      thumb.style.transform = 'translateY(' + y + 'px)';
      lastY = y;
    }
    setState('is-off', off);

    // A drawer just closed: the scroll restore and its settle land here. Re-sync
    // silently so closing the cart or search never flashes the bar.
    if (wasLocked) {
      wasLocked = false;
      settleUntil = performance.now() + UNLOCK_SETTLE_MS;
    }
    if (settleUntil && performance.now() < settleUntil) {
      lastTop = top;
      return;
    }

    // Sub-pixel jitter is not a scroll; lastTop only advances on real movement so slow
    // trackpad scrolls still accumulate past the threshold.
    if (Math.abs(top - lastTop) < 1) return;
    lastTop = top;
    clearTimeout(idle);
    if (off) {
      hide();
      return;
    }
    setState('is-active', true);
    idle = setTimeout(hide, IDLE_MS);
  }

  function schedule(e) {
    // The capture listener also hears nested scrollers (drawer bodies, snap rows);
    // they do not move the page, so skip their frames.
    var t = e && e.type === 'scroll' ? e.target : null;
    if (t && t !== document && t !== root && t !== body) return;
    if (!raf) raf = requestAnimationFrame(paint);
  }

  // Repaint the moment a drawer locks or unlocks, so the settle window starts when the
  // scroll restore lands. Without it, a drawer that closes without moving the page (the
  // menu) would leave the next real scroll held back for its first UNLOCK_SETTLE_MS.
  // Only lock flips schedule a frame; other body class changes cost one classList check.
  var lockedNow = isLocked();
  new MutationObserver(function () {
    var locked = isLocked();
    if (locked === lockedNow) return;
    lockedNow = locked;
    schedule();
  }).observe(body, { attributes: true, attributeFilter: ['class'] });

  // scroll does not bubble; one capture listener on document sees the document scroll.
  document.addEventListener('scroll', schedule, { passive: true, capture: true });
  window.addEventListener('resize', schedule, { passive: true });
  schedule();
})();
